import "./editpage.scss";
import * as React from "react";
import { type ReactNode } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { type SelectOptionType } from "@onshopfront/components";
import { throttle } from "throttle-debounce";
import { type DataSourceTables } from "../../Data/_Bases/DataSourceTables";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";
import { MDB_TYPE_DETAILS, type MDBTypes } from "../../Utilities/Hooks/MDBHooks";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Request from "../../Utilities/Platform/Request";
import SaveButton from "../Buttons/SaveButton";
import EditPageDialog, { type EditPageDialogType } from "./EditPageDialog";
import EditPageField, { type EditPageFieldType, type EditPageFieldValueType } from "./EditPageField";

export interface EditPageSection {
    icon: IconProp;
    name: string;
    content: React.ReactNode;
}

export interface EditSectionType {
    field: string;
    type?: EditPageFieldType;
    custom?: string;
    label?: string;
    disabled?: boolean | ((field: EditSectionType, classToCheck?: React.Component) => boolean);
    multiple?: boolean;
    clearable?: boolean;
    hidden?: boolean | ((field: EditSectionType, classToCheck: React.Component) => boolean);
    dataSource?: EditSectionSourceType;
    placeholder?: string;
    autoFocus?: boolean;
}

export interface EditSectionSourceType {
    type: "function" | "database" | "mdb";
    source: string;
    onCreate?: string | ((field: string, value: string) => void);
}

interface Props {
    sections: Array<EditPageSection>;
    onSave: (event: React.MouseEvent) => void;
    floatSave: boolean;
    additionalUpdateClasses: Array<string>;
    title: string;
    type: string;
    onClose: (e: React.MouseEvent) => void;
    className?: string;
    dialog?: EditPageDialogType;
    onDialogHide?: () => void;
    saveDisabled?: boolean;
}

interface State {
    collapsedSections: Array<string>;
    heights: Record<string, number>;
    scrollTop: number;
    scrollHeight: number;
}

export default class EditPage extends React.Component<Props, State> {
    public static defaultProps = {
        additionalUpdateClasses: [],
    };

    protected sectionReferences: Map<string, HTMLDivElement>;
    protected domObserver: false | MutationObserver;
    protected editRef: React.RefObject<HTMLDivElement | null>;
    protected menuRef: React.RefObject<HTMLDivElement | null>;
    protected pageRef: React.RefObject<HTMLDivElement | null>;

    constructor(props: Props) {
        super(props);

        this.state = {
            collapsedSections: [],
            heights          : {},
            scrollTop        : 0,
            scrollHeight     : 1,
        };

        this.sectionReferences = new Map();
        this.domObserver = false;

        this.editRef = React.createRef();
        this.menuRef = React.createRef();
        this.pageRef = React.createRef();

        this.handleMenuClick = this.handleMenuClick.bind(this);
        this.toggleSection = this.toggleSection.bind(this);
        this.handleScroll = throttle(250, this.handleScroll.bind(this));
        this.renderSection = this.renderSection.bind(this);
        this.handleObservation = this.handleObservation.bind(this);
        this.measureSections = throttle(250, this.measureSections.bind(this));
    }

    public componentDidMount(): void {
        this.measureSections();
        this.observe();

        setTimeout(this.handleScroll, 100);
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.sections.length !== this.props.sections.length) {
            this.measureSections();
        }

        if(!this.domObserver) {
            this.observe();
        }
    }

    public componentWillUnmount(): void {
        if(this.domObserver) {
            this.domObserver.disconnect();
        }
    }

    public measureSections(): void {
        const heights: Record<string, number> = {};

        for(const [ key, ref ] of this.sectionReferences) {
            if(!ref) {
                this.sectionReferences.delete(key);
                continue;
            }

            heights[key] = (ref.lastChild as HTMLElement).scrollHeight;
        }

        this.setState({
            heights,
        });
    }

    protected observe(): void {
        if(!this.pageRef.current) {
            return;
        }

        this.domObserver = new MutationObserver(this.handleObservation);
        this.domObserver.observe(this.pageRef.current, {
            childList    : true,
            subtree      : true,
            characterData: true,
        });
    }

    protected checkMutationNodes(nodes: NodeListOf<HTMLElement>, measureOnLoad: boolean): boolean {
        const updateClasses = [
            "edit-page-section-field",
            "edit-page-section-field-array-element",
            "edit-page-section-field-trackable",
            ...this.props.additionalUpdateClasses,
        ];

        for(let i = 0, l = nodes.length; i < l; i++) {
            if(typeof nodes[i].classList === "undefined") {
                continue;
            }

            for(let j = 0, n = updateClasses.length; j < n; j++) {
                if(
                    nodes[i].classList.contains(updateClasses[j]) ||
                    typeof nodes[i].dataset.editor !== "undefined" ||
                    typeof nodes[i].dataset.text !== "undefined" ||
                    nodes[i].tagName === "IMG" ||
                    nodes[i].tagName === "FIGURE"
                ) {
                    if(nodes[i].tagName === "IMG" && measureOnLoad) {
                        nodes[i].addEventListener("load", this.measureSections, {
                            capture: false,
                            passive: true,
                            once   : true,
                        });
                    } else if(nodes[i].tagName === "FIGURE") {
                        if(measureOnLoad) {
                            let currentNode = nodes[i].firstElementChild;

                            while(currentNode) {
                                if(currentNode.tagName === "IMG") {
                                    currentNode.addEventListener("load", this.measureSections, {
                                        capture: false,
                                        passive: true,
                                        once   : true,
                                    });

                                    return false;
                                }

                                currentNode = currentNode.firstElementChild;
                            }
                        }

                        continue;
                    }

                    return true;
                }
            }
        }

        return false;
    }

    protected handleObservation(mutations: Array<MutationRecord>): void {
        let measure = false;

        for(const mutation of mutations) {
            if(mutation.target.nodeName === "#text" && mutation.target.parentNode?.nodeName === "SPAN") {
                const parent = mutation.target.parentNode as HTMLElement;

                if(parent.dataset.text === "true") {
                    measure = true;
                    break;
                }
            }

            if(this.checkMutationNodes(mutation.addedNodes as NodeListOf<HTMLElement>, true)) {
                measure = true;
                break;
            }

            if(this.checkMutationNodes(mutation.removedNodes as NodeListOf<HTMLElement>, false)) {
                measure = true;
                break;
            }
        }

        if(measure) {
            this.measureSections();
        }
    }

    protected handleMenuClick(e: React.MouseEvent<HTMLDivElement>): void {
        const section = e.currentTarget.dataset.section as string;

        if(this.sectionReferences.has(section)) {
            (this.sectionReferences.get(section) as HTMLDivElement).scrollIntoView({
                block   : "start",
                inline  : "start",
                behavior: "smooth",
            });
        }
    }

    protected toggleSection(e: React.MouseEvent<HTMLDivElement>): void {
        const section = (e.currentTarget.parentNode as HTMLElement).dataset.section as string;

        this.setState(prevState => {
            const index = prevState.collapsedSections.indexOf(section);

            if(index === -1) {
                return {
                    collapsedSections: [
                        ...prevState.collapsedSections,
                        section,
                    ],
                };
            }

            return {
                collapsedSections: [
                    ...prevState.collapsedSections.slice(0, index),
                    ...prevState.collapsedSections.slice(index + 1),
                ],
            };
        }, this.handleScroll);
    }

    protected handleScroll(): void {
        if(!this.editRef.current) {
            return;
        }

        const edit = {
            height: this.editRef.current.scrollHeight,
            top   : this.editRef.current.scrollTop,
        };

        const windowHeight = window.innerHeight - 50; // 50 to take into account the header

        const menuItemHeight = 24;
        const menuItemPadding = 16;

        let topIndex = -1;
        let bottomIndex = 0;
        let windowLocation = 0;
        let broken = false;

        for(let i = 0, l = this.props.sections.length; i < l; i++) {
            const section = this.props.sections[i].name;

            if(windowLocation < edit.top) {
                topIndex++;
            }

            // Take into account padding and title
            let height = this.state.heights[section];

            // Title
            height += 24;

            // Title / Content border
            height += 1;

            // Title margin
            height += 8;

            // Outside border
            height += 2;

            // Padding
            height += 32;

            // Top padding
            if(i === 0) {
                height += 32;
            }

            // Margin between
            height += 32;

            // Need to remove final margin
            if(i + 1 === l) {
                height -= 32;
            }

            windowLocation += height;

            if(windowLocation >= edit.top + windowHeight) {
                broken = true;
                break;
            }

            bottomIndex++;
        }

        if(!broken) {
            bottomIndex--;
        }

        topIndex = Math.max(topIndex, 0);

        const scrollTop = topIndex * (menuItemHeight + (menuItemPadding * 2));
        let scrollBottom = bottomIndex * (menuItemHeight + (menuItemPadding * 2));

        scrollBottom += (menuItemHeight + (menuItemPadding * 2));

        const scrollHeight = scrollBottom - scrollTop;

        this.setState({
            scrollTop,
            scrollHeight,
        });
    }

    protected renderSection(section: EditPageSection): React.ReactNode {
        const classes = [ "edit-page-section" ];

        if(this.state.collapsedSections.includes(section.name)) {
            classes.push("edit-page-section-collapsed");
        }

        return (
            <div
                className={classes.join(" ")}
                key={section.name}
                data-section={section.name}
                ref={ref => {
                    if(!ref) {
                        this.sectionReferences.delete(section.name);
                    } else {
                        this.sectionReferences.set(
                            section.name,
                            ref
                        );
                    }
                }}
                data-testid={`edit-page-section-${section.name}`}
            >
                <div className="edit-page-section-title" onClick={this.toggleSection}>
                    <div className="edit-page-section-title-name">
                        <FontAwesomeLight icon={section.icon} />
                        <span>{section.name}</span>
                    </div>
                    <div className="edit-page-section-title-status">
                        <FontAwesomeLight icon="angle-down" />
                    </div>
                </div>
                <div
                    className="edit-page-section-content"
                    style={{
                        "--content-height": this.state.heights[section.name] + "px",
                    } as any}
                >
                    {section.content}
                </div>
            </div>
        );
    }

    public render(): React.ReactNode {
        const hasTitle = this.props.title ? !!this.props.title.trim() : false;

        const classes = [ "edit-page" ];

        if(this.props.className) {
            classes.push(this.props.className);
        }

        return (
            <div className={classes.join(" ")} ref={this.pageRef}>
                <div className="edit-page-menu">
                    <div className="edit-page-menu-scroll-wrapper" ref={this.menuRef}>
                        <div
                            className="edit-page-menu-position"
                            style={{
                                top   : this.state.scrollTop + "px",
                                height: this.state.scrollHeight + "px",
                            }}
                        />
                        {this.props.sections.map((section) => {
                            return (
                                <div
                                    className="edit-page-menu-item"
                                    key={section.name}
                                    data-section={section.name}
                                    onClick={this.handleMenuClick}
                                >
                                    <FontAwesomeLight icon={section.icon} />
                                    <span>{section.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="edit-page-content" ref={this.editRef} onScroll={this.handleScroll}>
                    <div className="edit-page-content-title">
                        {hasTitle ? this.props.title : (
                            <span className="edit-page-untitled">Untitled {this.props.type}</span>
                        )}
                    </div>
                    <div
                        className="edit-page-dialog-close"
                        onClick={this.props.onClose}
                        data-testid="edit-page-dialog-close"
                    >
                        <FontAwesomeLight icon="times-circle" />
                    </div>
                    {this.props.sections.map(this.renderSection)}
                    <div className={"edit-page-save" + (this.props.floatSave ? " float-save" : "")}>
                        <SaveButton
                            onClick={this.props.onSave}
                            data-testid="edit-page-save"
                            disabled={!!this.props.dialog || (this.props.saveDisabled ?? false)}
                        />
                    </div>
                </div>
                <EditPageDialog
                    dialog={this.props.dialog}
                    onHide={this.props.onDialogHide}
                />
            </div>
        );
    }
}

export function generateFieldName(field: string): string {
    const split = field.split(/(?=[A-Z])/);

    split[0] = uppercaseFirst(split[0]);

    return split.join(" ");
}

const _sourceFunctions = new Map();

export function createSearchDataSource(source: string): (search: string) => Promise<Array<SelectOptionType<string>>> {
    if(_sourceFunctions.has(source)) {
        return _sourceFunctions.get(source);
    }

    const callback = (search: string): Promise<Array<SelectOptionType<string>>> => {
        return determineDataSource()
            .then(dataSource => {
                return (dataSource[source as keyof DataSourceTables] as any).search(search);
            })
            .then((data: Array<{ name: string; uuid: string; }>) => {
                // Format the data
                return data.map(datum => ({
                    label: datum.name,
                    value: datum.uuid,
                })).sort((a, b) => {
                    return a.label.localeCompare(b.label);
                });
            });
    };

    _sourceFunctions.set(source, callback);

    return callback;
}

export function clearSearchDataSources(): void {
    _sourceFunctions.clear();
}

type FieldValueResultType = null | Array<SelectOptionType<string | number>> | SelectOptionType<string | number>;

interface LookupFieldState {
    selectedOptions: Record<string, FieldValueResultType>;
}

export async function lookupFieldValue(
    field: EditSectionType,
    modal: any,
    selectedOptions: Record<string, SelectOptionType<string>>,
    currentClass: React.Component<unknown, LookupFieldState>
): Promise<void> {
    // TODO: MAKE THIS FUNCTION WORK WITH RECURSIVE FIELD ADDRESSES (ie contact.addressStreet1)
    if(!modal[field.field]) {
        return;
    } else if(typeof modal[field.field].label !== "undefined") {
        return;
    } else if(typeof selectedOptions[field.field] !== "undefined") {
        return;
    }

    let resultField: FieldValueResultType = null;

    if(field.dataSource?.type === "database") {
        const source = await determineDataSource();

        if(field.multiple) {
            for(let i = 0, l = modal[field.field].length; i < l; i++) {
                const result = await (source[field.dataSource.source as keyof DataSourceTables] as any)
                    .get(modal[field.field][i]).catch(() => null);

                if(result) {
                    if(!Array.isArray(resultField)) {
                        resultField = [];
                    }

                    resultField.push({
                        label: result.name,
                        value: modal[field.field][i],
                    });
                }
            }
        } else {
            const result = await (source[field.dataSource.source as keyof DataSourceTables] as any)
                .get(modal[field.field]).catch(() => null);

            if(result) {
                resultField = {
                    label: result.name,
                    value: modal[field.field],
                };
            }
        }
    } else if(field.dataSource?.type === "mdb") {
        if(!field.dataSource.source) {
            return;
        }

        const type = MDB_TYPE_DETAILS[field.dataSource.source as MDBTypes];

        // TODO: write this in a less wasteful way
        const data: Array<{ id: number; name: string; }> = (await Request.get(type.url)).data;

        if(data.length) {
            if(field.multiple) {
                // TODO: CHECK TO SEE IF THIS WILL EVER BE MULTI-SELECT
            } else {
                const item = data.find(item => item.id === modal[field.field]);

                if(!item) {
                    return;
                } else {
                    resultField = {
                        label: item.name,
                        value: item.id,
                    };
                }
            }
        }
    } else if(field.dataSource?.source) {
        const fn = currentClass[field.dataSource.source as keyof typeof currentClass] as
            (field: EditSectionType, searchValue: boolean) => Promise<Array<{
                label: string;
                value: string;
            }>>;

        const result = await fn(
            modal[field.field],
            true
        );

        if(result.length) {
            if(field.multiple) {
                resultField = result;
            } else {
                resultField = result[0];
            }
        }
    }

    // Probably shouldn't do this
    currentClass.setState(prevState => {
        return {
            selectedOptions: {
                ...prevState.selectedOptions,
                [field.field]: resultField,
            },
        };
    });
}

export function generateEditFieldSection(
    section: Array<EditSectionType>,
    selectedOptions: Record<string, SelectOptionType<string>>,
    modal: any,
    currentClass: React.Component<unknown, LookupFieldState>,
    onChange: (field: string, value: EditPageFieldValueType) => void,
    ignoreFields?: Array<string>
): React.ReactNode {
    return (
        <React.Fragment>
            {section.map((field: EditSectionType) => {
                if(ignoreFields && ignoreFields.includes(field.field as string)) {
                    return;
                }

                if(typeof field.hidden === "function") {
                    if(field.hidden(field, currentClass)) {
                        return;
                    }
                } else if(typeof field.hidden === "boolean") {
                    if(field.hidden) {
                        return;
                    }
                }

                if(field.custom) {
                    const fn = currentClass[field.custom as keyof typeof currentClass] as
                        (field: EditSectionType) => ReactNode;

                    return fn(field);
                }

                let dataSource;

                if(field.dataSource && field.dataSource.type === "database") {
                    dataSource = createSearchDataSource(field.dataSource.source);
                }

                let loadOptions: undefined | ((search: string) => Promise<Array<SelectOptionType<string>>>);
                let onCreate: undefined |
                    ((field: string, value: string, callback: (label: string, value: string) => void) => void);

                if(typeof field.dataSource !== "undefined") {
                    if(field.dataSource.type !== "mdb") {
                        loadOptions = field.dataSource.type === "function" ?
                            (
                                currentClass[field.dataSource.source as keyof typeof currentClass] as
                                    (search: string) => Promise<Array<SelectOptionType<string>>>
                            ) :
                            dataSource;
                    }

                    lookupFieldValue(
                        field,
                        modal,
                        selectedOptions,
                        currentClass
                    );

                    if(typeof field.dataSource.onCreate === "string") {
                        onCreate = currentClass[field.dataSource.onCreate as keyof typeof currentClass] as
                            (field: string, value: string, callback: (label: string, value: string) => void) => void;
                    } else if(typeof field.dataSource.onCreate === "function") {
                        onCreate = field.dataSource.onCreate;
                    }
                }

                const valueAddress = field.field.toString().split(".");
                let value = modal;

                for(let i = 0, l = valueAddress.length; i < l; i++) {
                    if(Object.prototype.hasOwnProperty.call(value, valueAddress[i])) {
                        value = value[valueAddress[i]];
                    } else {
                        value = "";
                        break;
                    }
                }

                let disabled = false;

                if(typeof field.disabled === "boolean") {
                    disabled = field.disabled;
                } else if(typeof field.disabled === "function") {
                    disabled = field.disabled(field, currentClass);
                }

                let label = field.label;

                if(typeof label === "undefined") {
                    label = generateFieldName(field.field as string);
                }

                return (
                    <EditPageField
                        label={label}
                        field={field.field.toString()}
                        value={value}
                        type={field.type as EditPageFieldType}
                        onChange={onChange}
                        disabled={disabled}
                        key={field.field}
                        selectedOption={selectedOptions[field.field]}
                        loadOptions={loadOptions}
                        multiple={field.multiple}
                        onCreate={onCreate}
                        clearable={field.clearable}
                        source={field.dataSource}
                        placeholder={field.placeholder}
                        autoFocus={field.autoFocus || false}
                    />
                );
            })}
        </React.Fragment>
    );
}

export function generateSelectResultsFromObject(
    options: Record<string, string>,
    search: string,
    searchValue = false
): Array<{ label: string; value: string; }> {
    const keyMap = [];
    const valueMap = [];

    for(const i in options) {
        if(!Object.prototype.hasOwnProperty.call(options, i)) continue;

        keyMap.push(i);
        valueMap.push(options[i]);
    }

    let searchArray;

    if(searchValue) {
        searchArray = keyMap;
    } else {
        searchArray = valueMap;
    }

    const results = [];
    const s = search.toLowerCase();

    for(let i = 0, l = searchArray.length; i < l; i++) {
        if(searchArray[i].toLowerCase().includes(s)) {
            results.push({
                label: valueMap[i],
                value: keyMap[i],
            });
        }
    }

    return results;
}
